import React, { useEffect, useState, useRef } from "react";
import api, { apiErr } from "../lib/api";
import { useSearchParams } from "react-router-dom";
import { useSelection, bulkDelete } from "../lib/bulk";
import { usePrivacy, Private } from "../context/PrivacyContext";
import { printSection } from "../lib/print";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { IcdDescriptions } from "../components/IcdDescriptions";
import { Letterhead } from "../components/Letterhead";
import { PageHeader, Btn, Card, Badge, inputCls, Modal } from "../components/ui-kit";
import { ManagedSelect } from "../components/ManagedSelect";
import { fmtDate } from "../lib/date";
import { Plus, Trash2, FilePlus2, Save, FileDown, Printer, NotebookPen, ChevronRight, Eye, Pencil, Copy, Loader2, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { AddIcdCodeModal } from "../components/AddIcdCodeModal";
import { byFirstName } from "../lib/utils";

let seq = 0;
const newItem = () => ({ _uid: `it-${seq++}`, cpt_code: "", description: "", quantity: 1, minutes: 15, amount: 0 });
// Full-hour therapy codes default to 4 units (4 × 15-min = 60 min) since that's how they're billed.
const FOUR_UNIT_CODES = ["90837", "90791"];
const defaultQtyFor = (code) => (FOUR_UNIT_CODES.includes(code) ? 4 : 1);
const emptyInvoice = {
  patient_id: "", patient_name: "", dob: "", ssn: "", policy_number: "", gender: "",
  invoice_number: "", service_date: new Date().toISOString().slice(0, 10),
  visit_reason: "", icd10: "", provider: "", status: "unpaid",
};
const reasons = ["General Consultation", "Physical Therapy", "Therapeutic Massage", "Relaxing Massage", "Evaluation", "Follow-up", "Re-evaluation", "Psychotherapy", "Group Therapy"];
const STATUSES = ["unpaid", "in_transit", "paid", "denied"];
const statusTone = { paid: "green", in_transit: "amber", denied: "red", unpaid: "amber", void: "gray", outstanding: "amber" };
const STATUS_SELECT_CLASS = {
  green: "bg-moneygreen-100 text-moneygreen-700 border-moneygreen-200",
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
};
const statusSelectClass = (status) => STATUS_SELECT_CLASS[statusTone[status]] || STATUS_SELECT_CLASS.amber;
const statusKey = { in_transit: "inTransit", paid: "paid", denied: "denied", unpaid: "unpaid", void: "draft", outstanding: "outstanding" };



const cellCls = "w-full px-2 py-1.5 rounded-md bg-white border border-border focus:outline-none focus:ring-2 focus:ring-moneygreen-500 text-sm";

export default function Invoices() {
  const { user } = useAuth();
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get("status");
  const sel = useSelection();
  const [settings, setSettings] = useState(null);
  const [patients, setPatients] = useState([]);
  const [cpt, setCpt] = useState([]);
  const [providers, setProviders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [inv, setInv] = useState(emptyInvoice);
  const [items, setItems] = useState([newItem()]);
  const [editId, setEditId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [noteModal, setNoteModal] = useState(false);
  const [notePatient, setNotePatient] = useState("");
  const [billingNotes, setBillingNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [icdHistory, setIcdHistory] = useState([]);
  const [icdCatalog, setIcdCatalog] = useState([]);
  const [addIcdOpen, setAddIcdOpen] = useState(false);
  const [icdHistOpen, setIcdHistOpen] = useState(false);
  const [icdAllOpen, setIcdAllOpen] = useState(false);
  const icdHistRef = useRef(null);
  const icdAllRef = useRef(null);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [deletingInvoice, setDeletingInvoice] = useState("");

  const statusLabel = (s) => t(statusKey[s] || s);
  const loadInvoices = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  const loadNumber = () => api.get("/invoices/next-number").then((r) => setInv((s) => ({ ...s, invoice_number: r.data.invoice_number }))).catch(() => {});
  const loadIcdHistory = (pid) => {
    if (!pid) { setIcdHistory([]); return; }
    api.get("/invoices/patient-code-history", { params: { patient_id: pid } })
      .then((r) => setIcdHistory(r.data.icd10 || [])).catch(() => setIcdHistory([]));
  };

  const dismissIcd = async (code) => {
    const pid = inv.patient_id;
    if (!pid) return;
    try {
      await api.post("/invoices/patient-code-history/dismiss", { patient_id: pid, code });
      setIcdHistory((h) => h.filter((c) => c !== code));
      toast.success(t("icdRemoved"));
    } catch { toast.error("Failed"); }
  };

  useEffect(() => {
    const onDown = (e) => {
      if (icdHistRef.current && !icdHistRef.current.contains(e.target)) setIcdHistOpen(false);
      if (icdAllRef.current && !icdAllRef.current.contains(e.target)) setIcdAllOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/cpt-codes").then((r) => setCpt(r.data)).catch(() => {});
    api.get("/icd-codes").then((r) => setIcdCatalog(r.data)).catch(() => {});
    api.get("/users").then((r) => setProviders(r.data.filter((u) => ["doctor", "psychologist", "nurse", "admin"].includes(u.role)))).catch(() => {});
    loadInvoices();
    loadNumber();
  }, []);

  // Deep link from a note or patient page: /invoices?from_note=<id>&patient_id=<id>
  useEffect(() => {
    const noteId = params.get("from_note");
    const pid = params.get("patient_id");
    if (!noteId || cpt.length === 0) return;
    api.get("/notes/for-billing", { params: { patient_id: pid } })
      .then((r) => {
        const n = (r.data || []).find((x) => x.id === noteId);
        if (n) applyNote(n, pid);
        else toast.error(t("noteNotFound"));
      })
      .catch((err) => toast.error(apiErr(err)))
      .finally(() => { params.delete("from_note"); params.delete("patient_id"); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpt, patients]);

  const shownInvoices = (statusFilter
    ? invoices.filter((v) => (statusFilter === "outstanding" ? v.status !== "paid" : v.status === statusFilter))
    : invoices
  ).filter((v) => {
    const q = invoiceSearch.trim().toLowerCase();
    if (!q) return true;
    const name = (v.patient_name || "").toLowerCase();
    const date = (v.service_date || "").toLowerCase();
    const displayDate = (fmtDate(v.service_date) || "").toLowerCase();
    return name.includes(q) || date.includes(q) || displayDate.includes(q);
  });

  const onPatient = async (e) => {
    const pid = e.target.value;
    if (!pid) { setInv((s) => ({ ...s, patient_id: "", patient_name: "", dob: "", ssn: "", gender: "" })); setIcdHistory([]); return; }
    let p = patients.find((x) => x.id === pid);
    try { p = (await api.get(`/patients/${pid}`)).data; } catch { /* fallback to cached */ }
    setInv((s) => ({
      ...s, patient_id: pid,
      patient_name: p ? `${p.first_name} ${p.last_name}` : "",
      dob: p?.dob || "", gender: p?.gender || "", ssn: p?.ssn || "",
    }));
    loadIcdHistory(pid);
  };
  const setF = (k) => (e) => setInv({ ...inv, [k]: e.target.value });
  const isPsychologist = (name) => providers.some((u) => u.role === "psychologist" && u.name === name);
  const setProvider = (e) => setInv((s) => ({ ...s, provider: e.target.value,
    ...(isPsychologist(e.target.value) && !s.visit_reason ? { visit_reason: "Psychotherapy" } : {}) }));

  const setItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const onCode = (idx, code) => {
    const c = cpt.find((x) => x.code === code);
    const qty = defaultQtyFor(code);
    setItem(idx, { cpt_code: code, description: c?.description || "", amount: c?.amount || 0, quantity: qty, minutes: qty * 15 });
    if (FOUR_UNIT_CODES.includes(code)) setInv((s) => (s.visit_reason ? s : { ...s, visit_reason: "Psychotherapy" }));
  };
  const total = items.reduce((s, i) => s + (Number(i.amount) || 0) * (Number(i.quantity) || 0), 0);

  const resetInvoice = () => { setEditId(null); setInv(emptyInvoice); setItems([newItem()]); setIcdHistory([]); loadNumber(); };

  const openNoteModal = () => { setNotePatient(""); setBillingNotes([]); setNoteModal(true); };
  const onNotePatient = (e) => {
    const pid = e.target.value;
    setNotePatient(pid);
    setBillingNotes([]);
    if (!pid) return;
    setLoadingNotes(true);
    api.get("/notes/for-billing", { params: { patient_id: pid } })
      .then((r) => setBillingNotes(r.data))
      .catch((err) => toast.error(apiErr(err)))
      .finally(() => setLoadingNotes(false));
  };
  const applyNote = (n, pid) => {
    const patientId = pid || notePatient;
    const p = patients.find((x) => x.id === patientId);
    setInv((s) => ({
      ...s,
      patient_id: patientId,
      patient_name: p ? `${p.first_name} ${p.last_name}` : s.patient_name,
      dob: n.dob || p?.dob || s.dob,
      gender: n.gender || p?.gender || s.gender,
      ssn: n.ssn || s.ssn,
      service_date: n.visit_date || (n.created_at || "").slice(0, 10) || s.service_date,
      visit_reason: isPsychologist(n.attending_provider) || n.cpt_code === "90837" ? "Psychotherapy" : (n.reason_for_visit || "").trim(),
      icd10: n.icd10 || s.icd10,
      provider: n.attending_provider || s.provider,
    }));
    if (n.cpt_code) {
      const c = cpt.find((x) => x.code === n.cpt_code);
      const qty = defaultQtyFor(n.cpt_code);
      setItems([{ _uid: `it-${seq++}`, cpt_code: n.cpt_code, description: c?.description || "", quantity: qty, minutes: qty * 15, amount: c?.amount || 0 }]);
    }
    setNoteModal(false);
    if (!isPsychologist(n.attending_provider) && n.cpt_code !== "90837" && !(n.reason_for_visit || "").trim()) toast.warning(t("noteNoReason"), { duration: 10000 });
    loadIcdHistory(patientId);
    toast.success(t("prefilledFromNote"));
  };
  const reasonOptions = inv.visit_reason && !reasons.includes(inv.visit_reason)
    ? [inv.visit_reason, ...reasons] : reasons;

  const save = async () => {
    const valid = items.filter((i) => i.cpt_code || i.description).map(({ _uid, ...rest }) => rest);
    if (!inv.patient_id && !inv.patient_name) { toast.error(t("patient")); return; }
    if (valid.length === 0) { toast.error(t("addItem")); return; }
    try {
      let saved;
      if (editId) { saved = await api.put(`/invoices/${editId}`, { ...inv, items: valid }); toast.success(t("updated")); }
      else { saved = await api.post("/invoices", { ...inv, items: valid }); toast.success(t("save") + " ✓"); }
      window.localStorage.setItem("vpp_invoice_saved_at", String(Date.now()));
      window.dispatchEvent(new CustomEvent("vpp:invoice-saved", { detail: saved.data }));
      resetInvoice(); loadInvoices();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const editInvoice = async (id) => {
    try {
      const r = await api.get(`/invoices/${id}`);
      const d = r.data;
      setEditId(id);
      setInv({
        patient_id: d.patient_id || "", patient_name: d.patient_name || "", dob: d.dob || "",
        ssn: d.ssn || "", policy_number: d.policy_number || "", gender: d.gender || "",
        invoice_number: d.invoice_number || "", service_date: d.service_date || new Date().toISOString().slice(0, 10),
        visit_reason: d.visit_reason || "", icd10: d.icd10 || "", icd10_descriptions: d.icd10_descriptions || [], provider: d.provider || "", status: d.status || "in_transit",
      });
      setItems((d.items || []).length ? d.items.map((it) => ({ _uid: `it-${seq++}`, ...it, minutes: (Number(it.quantity) || 0) * 15 })) : [newItem()]);
      setViewing(null);
      loadIcdHistory(d.patient_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiErr(err)); }
  };

  const viewInvoice = async (id) => {
    try { setViewing((await api.get(`/invoices/${id}`)).data); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const duplicateInvoice = async (id) => {
    try {
      const d = (await api.get(`/invoices/${id}`)).data;
      setEditId(null);
      setViewing(null);
      setInv({
        patient_id: d.patient_id || "", patient_name: d.patient_name || "", dob: d.dob || "",
        ssn: d.ssn || "", policy_number: d.policy_number || "", gender: d.gender || "",
        invoice_number: "", service_date: new Date().toISOString().slice(0, 10),
        visit_reason: d.visit_reason || "", icd10: d.icd10 || "", provider: d.provider || "", status: "unpaid",
      });
      setItems((d.items || []).length ? d.items.map((it) => ({ _uid: `it-${seq++}`, ...it, minutes: (Number(it.quantity) || 0) * 15 })) : [newItem()]);
      loadNumber();
      loadIcdHistory(d.patient_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success(t("duplicated"));
    } catch (err) { toast.error(apiErr(err)); }
  };

  const changeStatus = async (id, status) => {
    try { await api.put(`/invoices/${id}/status`, null, { params: { status } }); toast.success(t("updated")); loadInvoices(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const removeInvoice = async (id) => {
    if (!window.confirm(t("confirmDeleteInvoice"))) return;
    setDeletingInvoice(id);
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("delete") + " ✓");
      loadInvoices();
    } catch (err) { toast.error(apiErr(err), { duration: Infinity }); }
    finally { setDeletingInvoice(""); }
  };
  const removeSelected = async () => {
    try { await bulkDelete("/invoices/bulk-delete", [...sel.selected], t); sel.clear(); loadInvoices(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  // Only one invoice prints at a time: when a saved invoice is being viewed,
  // the view modal owns the #invoice-print id; otherwise the editor owns it.
  const editorPrintId = viewing ? "editor-doc" : "invoice-print";

  return (
    <div>
      <div className="no-print">
        <PageHeader title={t("invoices")} subtitle={editId ? `${t("editInvoice")} · ${inv.invoice_number}` : ""}
          action={<div className="flex flex-wrap gap-2">
            <Btn variant="outline" onClick={openNoteModal} data-testid="create-from-note-btn"><NotebookPen className="w-4 h-4" />{t("createFromNote")}</Btn>
            <Btn variant="outline" onClick={resetInvoice} data-testid="new-invoice-btn"><FilePlus2 className="w-4 h-4" />{t("newInvoice")}</Btn>
            <Btn onClick={save} data-testid="save-invoice-btn"><Save className="w-4 h-4" />{editId ? t("save") : t("saveInvoice")}</Btn>
            <Btn variant="outline" onClick={() => { if (editId) api.post(`/invoices/${editId}/to-folder`).then(() => toast.success(t("savedToFolder"))).catch(() => {}); window.print(); }} data-testid="save-pdf-btn"><FileDown className="w-4 h-4" />{t("saveAsPdf")}</Btn>
            <Btn variant="outline" onClick={() => window.print()} data-testid="print-btn"><Printer className="w-4 h-4" />{t("print")}</Btn>
          </div>} />
      </div>

      {/* Invoice editor document */}
      <Card className="p-6 sm:p-8">
        <div id={editorPrintId}>
          <Letterhead settings={settings} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            <div>
              <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-moneygreen-600 border-b border-border pb-2 mb-3">{t("patientInformation")}</h3>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="col-span-2">
                  <CField label={t("fullName")}>
                    <ManagedSelect listKey="patients" value={inv.patient_id} onChange={onPatient} className={cellCls} data-testid="inv-patient">
                      <option value="">{t("selectPatient")}</option>
                      {byFirstName(patients).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                    </ManagedSelect>
                  </CField>
                </div>
                <CField label={t("dob")}>
                  <input type="date" value={inv.dob || ""} onChange={setF("dob")} className={`${cellCls} no-print`} data-testid="inv-dob" />
                  <span className="print-only text-sm text-moneygreen-800">{fmtDate(inv.dob) || "—"}</span>
                </CField>
                <CField label="SSN"><input value={inv.ssn || ""} onChange={setF("ssn")} readOnly={!user?.can_view_full_ssn} title={user?.can_view_full_ssn ? "" : t("ssnNoAccess")} className={cellCls} data-testid="inv-ssn" placeholder="XXX-XX-XXXX" /></CField>
                <CField label={t("policyNumber")}><input value={inv.policy_number || ""} onChange={setF("policy_number")} className={cellCls} data-testid="inv-policy" /></CField>
                <CField label={t("gender")}>
                  <select value={inv.gender || ""} onChange={setF("gender")} className={cellCls}>
                    <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </CField>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-moneygreen-600 border-b border-border pb-2 mb-3">{t("invoiceInformation")}</h3>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                <CField label={t("invoiceNumber")}><input value={inv.invoice_number} onChange={setF("invoice_number")} className={cellCls} data-testid="inv-number" /></CField>
                <CField label={t("serviceDate")}>
                  <input type="date" value={inv.service_date} onChange={setF("service_date")} className={`${cellCls} no-print`} data-testid="inv-service-date" />
                  <span className="print-only text-sm text-moneygreen-800">{fmtDate(inv.service_date) || "—"}</span>
                </CField>
                <CField label={t("visitReason")}>
                  <select value={inv.visit_reason || ""} onChange={setF("visit_reason")} className={cellCls} data-testid="inv-reason">
                    <option value="">—</option>
                    {reasonOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </CField>
                <CField label="ICD-10-CM">
                  <div className="flex gap-1.5">
                    <div className="relative flex-1" ref={icdHistRef}>
                      <button type="button" onClick={() => setIcdHistOpen((o) => !o)} data-testid="inv-icd10"
                        className={cellCls + " flex items-center justify-between text-left gap-1"}>
                        <span className={inv.icd10 ? "font-mono text-moneygreen-800 truncate" : "text-stone-400 truncate"}>{inv.icd10 || t("usedBefore")}</span>
                        <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" />
                      </button>
                      {icdHistOpen && (
                        <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded-md shadow-lg max-h-56 overflow-auto" data-testid="inv-icd-history-panel">
                          {icdHistory.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-stone-400">{t("noCodes")}</div>
                          ) : icdHistory.map((c) => (
                            <div key={c} className="flex items-center justify-between hover:bg-moneygreen-50">
                              <button type="button" onClick={() => { setF("icd10")({ target: { value: c } }); setIcdHistOpen(false); }}
                                data-testid={`inv-icd-opt-${c}`} className="flex-1 text-left px-3 py-1.5 font-mono text-sm text-moneygreen-800">{c}</button>
                              <button type="button" onClick={() => dismissIcd(c)} data-testid={`inv-icd-opt-remove-${c}`} title={t("removeIcdSuggestion")}
                                className="px-2 py-1.5 text-stone-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="relative" ref={icdAllRef}>
                      <button type="button" onClick={() => setIcdAllOpen((o) => !o)} title={t("allCodes")} data-testid="inv-icd10-add"
                        className="shrink-0 px-2 h-full rounded-md border border-moneygreen-600 text-moneygreen-700 hover:bg-moneygreen-50 transition-colors flex items-center"><Plus className="w-4 h-4" /></button>
                      {icdAllOpen && (
                        <div className="absolute z-30 mt-1 right-0 w-72 bg-white border border-border rounded-md shadow-lg max-h-64 overflow-auto" data-testid="inv-icd-all-panel">
                          {icdCatalog.map((c) => (
                            <button key={c.code} type="button" onClick={() => { setF("icd10")({ target: { value: c.code } }); setIcdAllOpen(false); }}
                              data-testid={`inv-icd-all-${c.code}`} className="w-full text-left px-3 py-1.5 text-sm text-stone-700 hover:bg-moneygreen-50">
                              <span className="font-mono font-semibold text-moneygreen-800">{c.code}</span>{c.description ? ` — ${c.description}` : ""}
                            </button>
                          ))}
                          <button type="button" onClick={() => { setIcdAllOpen(false); setAddIcdOpen(true); }} data-testid="inv-icd-add-new"
                            className="w-full text-left px-3 py-1.5 text-xs text-moneygreen-700 font-semibold border-t border-border hover:bg-moneygreen-50 flex items-center gap-1"><Plus className="w-3.5 h-3.5" />{t("newIcdCode")}</button>
                        </div>
                      )}
                    </div>
                  </div>
                  <IcdDescriptions value={inv.icd10} saved={inv.icd10_descriptions} catalog={icdCatalog} t={t} />
                </CField>
                <CField label={t("attendingProvider")}>
                  <ManagedSelect listKey="providers" value={inv.provider || ""} onChange={setProvider} className={cellCls} data-testid="inv-provider">
                    <option value="">—</option>
                    {providers.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </ManagedSelect>
                </CField>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-moneygreen-800 text-tan-100 text-left text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 w-8">#</th>
                  <th className="px-3 py-2 w-40">{t("cptCode")}</th>
                  <th className="px-3 py-2">{t("serviceDescription")}</th>
                  <th className="px-3 py-2 w-20">{t("units")}</th>
                  <th className="px-3 py-2 w-24">{t("minutes")}</th>
                  <th className="px-3 py-2 w-28">{t("unitPrice")}</th>
                  <th className="px-3 py-2 w-28 text-right">{t("fee")}</th>
                  <th className="px-3 py-2 w-8 no-print"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it._uid} className="border-t border-border" data-testid={`inv-item-${idx}`}>
                    <td className="px-3 py-2 text-stone-500">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <ManagedSelect listKey="cpt" value={it.cpt_code} onChange={(e) => onCode(idx, e.target.value)} className={cellCls} data-testid={`inv-item-cpt-${idx}`}>
                        <option value="">{t("selectCpt")}</option>
                        {cpt.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                      </ManagedSelect>
                    </td>
                    <td className="px-3 py-2"><input value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} className={cellCls} data-testid={`inv-item-desc-${idx}`} /></td>
                    <td className="px-3 py-2"><input type="number" min="0" value={it.quantity} onChange={(e) => { const q = Math.max(0, +e.target.value || 0); setItem(idx, { quantity: q, minutes: q * 15 }); }} className={cellCls} data-testid={`inv-item-units-${idx}`} /></td>
                    <td className="px-3 py-2"><input type="number" value={it.minutes} readOnly tabIndex={-1} className={`${cellCls} bg-tan-50 text-stone-500 cursor-not-allowed`} data-testid={`inv-item-minutes-${idx}`} /></td>
                    <td className="px-3 py-2"><input type="number" min="0" step="0.01" value={it.amount} onChange={(e) => setItem(idx, { amount: +e.target.value || 0 })} className={cellCls} data-testid={`inv-item-price-${idx}`} /></td>
                    <td className="px-3 py-2 text-right font-semibold text-moneygreen-800" data-testid={`inv-item-fee-${idx}`}>${((Number(it.amount) || 0) * (Number(it.quantity) || 0)).toFixed(2)}</td>
                    <td className="px-3 py-2 no-print">
                      {items.length > 1 && <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-destructive"><Trash2 className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <Btn variant="outline" onClick={() => setItems([...items, newItem()])} data-testid="add-service-btn" className="no-print"><Plus className="w-4 h-4" />{t("addService")}</Btn>
            <div className="text-right ml-auto">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mr-4">{t("total")}</span>
              <span className="font-heading text-3xl font-extrabold text-moneygreen-800" data-testid="invoice-total">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Saved invoices */}
      <div className="mt-8 no-print">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h3 className="font-heading text-lg font-bold text-moneygreen-800">{t("savedInvoices")}</h3>
          <input type="text" value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)}
            placeholder={t("searchInvoicesPlaceholder")} data-testid="invoice-search"
            className={`${inputCls} !w-auto max-w-xs`} />
          {statusFilter && (
            <Badge tone={statusTone[statusFilter] || "gray"} className="flex items-center gap-1" data-testid="invoice-status-filter">
              {statusLabel(statusFilter)}
              <button onClick={() => { params.delete("status"); setParams(params); }} data-testid="clear-invoice-filter" className="ml-1 underline">{t("clearFilter")}</button>
            </Badge>
          )}
          {shownInvoices.length > 0 && (
            <div className="ml-auto flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-sm text-stone-600 cursor-pointer">
                <input type="checkbox" data-testid="select-all-invoices"
                  checked={sel.count >= shownInvoices.length && shownInvoices.length > 0}
                  onChange={() => sel.toggleAll(shownInvoices.map((v) => v.id))}
                  className="w-4 h-4 accent-moneygreen-600 cursor-pointer" />
                {t("selectAll")}
              </label>
              {sel.count > 0 && (<>
                <span className="text-sm text-stone-500">{sel.count}</span>
                <Btn variant="danger" onClick={removeSelected} data-testid="bulk-delete-invoices"><Trash2 className="w-4 h-4" />{t("deleteSelected")}</Btn>
                <Btn variant="outline" onClick={sel.clear} data-testid="deselect-invoices">{t("deselectAll")}</Btn>
              </>)}
            </div>
          )}
        </div>
        <Card className="overflow-hidden">
          {shownInvoices.length === 0 ? <p className="p-6 text-sm text-stone-400">{t("noData")}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-stone-500 border-b border-border">
                    <th className="px-4 py-3 w-8">
                      <input type="checkbox" data-testid="select-all-invoices-head"
                        checked={sel.count >= shownInvoices.length && shownInvoices.length > 0}
                        onChange={() => sel.toggleAll(shownInvoices.map((v) => v.id))}
                        className="w-4 h-4 accent-moneygreen-600 cursor-pointer" />
                    </th>
                    <th className="px-5 py-3">{t("invoiceNumber")}</th>
                    <th className="px-5 py-3">{t("patient")}</th>
                    <th className="px-5 py-3 hidden md:table-cell">{t("serviceDate")}</th>
                    <th className="px-5 py-3 hidden lg:table-cell">{t("completedOn")}</th>
                    <th className="px-5 py-3">{t("status")}</th>
                    <th className="px-5 py-3 text-right">{t("total")}</th>
                    <th className="px-5 py-3 text-right">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownInvoices.map((v, i) => (
                    <tr key={v.id} data-testid={`invoice-row-${v.id}`} className={`border-b border-border/60 ${sel.has(v.id) ? "bg-moneygreen-50" : i % 2 ? "bg-tan-50/40" : ""}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={sel.has(v.id)} onChange={() => sel.toggle(v.id)}
                          data-testid={`select-invoice-${v.id}`} className="w-4 h-4 accent-moneygreen-600 cursor-pointer" />
                      </td>
                      <td className="px-5 py-3 font-mono font-semibold text-moneygreen-700 cursor-pointer hover:underline" onClick={() => viewInvoice(v.id)} data-testid={`open-invoice-${v.id}`}>{v.invoice_number || "—"}</td>
                      <td className="px-5 py-3 text-stone-700 cursor-pointer" onClick={() => viewInvoice(v.id)}><Private value={v.patient_name} /></td>
                      <td className="px-5 py-3 hidden md:table-cell text-stone-600 cursor-pointer" onClick={() => viewInvoice(v.id)}>{fmtDate(v.service_date || (v.created_at || "").slice(0, 10))}</td>
                      <td className="px-5 py-3 hidden lg:table-cell text-stone-600" data-testid={`invoice-completed-${v.id}`}>{v.completed_at ? fmtDate(v.completed_at.slice(0, 10)) : "—"}</td>
                      <td className="px-5 py-3">
                        <select value={STATUSES.includes(v.status) ? v.status : "in_transit"} onChange={(e) => changeStatus(v.id, e.target.value)}
                          data-testid={`invoice-status-${v.id}`} title={t("changeStatus")}
                          className={`px-2 py-1 rounded-md border text-xs font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-moneygreen-500 ${statusSelectClass(v.status)}`}>
                          {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-moneygreen-800">${(v.total || 0).toFixed(2)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Btn variant="ghost" onClick={() => viewInvoice(v.id)} data-testid={`view-invoice-${v.id}`} className="!px-2" title={t("view")}><Eye className="w-4 h-4" /></Btn>
                          <Btn variant="ghost" onClick={() => editInvoice(v.id)} data-testid={`edit-invoice-${v.id}`} className="!px-2" title={t("edit")}><Pencil className="w-4 h-4" /></Btn>
                          <Btn variant="ghost" onClick={() => duplicateInvoice(v.id)} data-testid={`duplicate-invoice-${v.id}`} className="!px-2" title={t("duplicate")}><Copy className="w-4 h-4" /></Btn>
                          <Btn variant="ghost" onClick={() => removeInvoice(v.id)} disabled={!!deletingInvoice}
                            data-testid={`delete-invoice-${v.id}`} className="!px-2 !text-destructive" title={t("delete")}>
                            {deletingInvoice === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* View single invoice */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing ? `${t("invoiceNumber")} ${viewing.invoice_number || ""}` : ""} wide>
        {viewing && (
          <div className="space-y-4">
            <div id="invoice-print" data-testid="invoice-view">
              <Letterhead settings={settings} />
              <div className="flex items-center justify-between mt-3">
                <div>
                  <p className="font-mono font-bold text-moneygreen-700">{viewing.invoice_number}</p>
                  <p className="text-sm text-stone-500">{fmtDate(viewing.service_date || (viewing.created_at || "").slice(0, 10))}</p>
                </div>
                <Badge tone={statusTone[viewing.status] || "gray"}>{statusLabel(viewing.status)}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-5">
                <div>
                  <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-moneygreen-600 border-b border-border pb-1.5 mb-2">{t("patientInformation")}</h4>
                  <div className="space-y-1 text-sm">
                    <VRow label={t("fullName")} value={viewing.patient_name ? <Private value={viewing.patient_name} /> : ""} />
                    <VRow label={t("dob")} value={viewing.dob ? <Private value={viewing.dob} /> : ""} />
                    <VRow label="SSN" value={viewing.ssn ? <Private value={viewing.ssn} /> : ""} />
                    <VRow label={t("gender")} value={viewing.gender} />
                    <VRow label={t("policyNumber")} value={viewing.policy_number} />
                  </div>
                </div>
                <div>
                  <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-moneygreen-600 border-b border-border pb-1.5 mb-2">{t("invoiceInformation")}</h4>
                  <div className="space-y-1 text-sm">
                    <VRow label={t("invoiceNumber")} value={viewing.invoice_number} />
                    <VRow label={t("serviceDate")} value={fmtDate(viewing.service_date)} />
                    <VRow label={t("completed")} value={viewing.completed_at ? fmtDate((viewing.completed_at || "").slice(0, 10)) : ""} />
                    <VRow label={t("status")} value={statusLabel(viewing.status)} />
                    <VRow label={t("visitReason")} value={viewing.visit_reason} />
                    <VRow label="ICD-10-CM" value={viewing.icd10 && <><IcdDescriptions value={viewing.icd10} saved={viewing.icd10_descriptions} catalog={icdCatalog} t={t} testPrefix="view" /></>} />
                    <VRow label={t("attendingProvider")} value={viewing.provider} />
                  </div>
                </div>
              </div>
              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-moneygreen-800 text-tan-100 text-left text-xs uppercase tracking-wider">
                      <th className="px-3 py-2">{t("cptCode")}</th>
                      <th className="px-3 py-2">{t("serviceDescription")}</th>
                      <th className="px-3 py-2 w-20">{t("units")}</th>
                      <th className="px-3 py-2 w-24">{t("minutes")}</th>
                      <th className="px-3 py-2 w-28">{t("unitPrice")}</th>
                      <th className="px-3 py-2 w-28 text-right">{t("fee")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewing.items || []).map((it, idx) => (
                      <tr key={`${it.cpt_code || "row"}-${idx}`} className="border-t border-border">
                        <td className="px-3 py-2 font-mono">{it.cpt_code || "—"}</td>
                        <td className="px-3 py-2">{it.description}</td>
                        <td className="px-3 py-2">{it.quantity}</td>
                        <td className="px-3 py-2">{(Number(it.quantity) || 0) * 15}</td>
                        <td className="px-3 py-2">${(Number(it.amount) || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-moneygreen-800">${((Number(it.amount) || 0) * (Number(it.quantity) || 0)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right mt-4">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mr-4">{t("total")}</span>
                <span className="font-heading text-3xl font-extrabold text-moneygreen-800">${(viewing.total || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 no-print">
              <Btn variant="outline" onClick={() => editInvoice(viewing.id)} data-testid="view-edit-invoice-btn"><Pencil className="w-4 h-4" />{t("edit")}</Btn>
              <Btn variant="outline" onClick={() => duplicateInvoice(viewing.id)} data-testid="view-duplicate-invoice-btn"><Copy className="w-4 h-4" />{t("duplicate")}</Btn>
              <Btn variant="outline" onClick={() => { if (viewing?.id) api.post(`/invoices/${viewing.id}/to-folder`).then(() => toast.success(t("savedToFolder"))).catch(() => {}); printSection("invoice-print"); }} data-testid="view-save-pdf-btn"><FileDown className="w-4 h-4" />{t("saveAsPdf")}</Btn>
              <Btn variant="outline" onClick={() => printSection("invoice-print")} data-testid="view-print-btn"><Printer className="w-4 h-4" />{t("print")}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={noteModal} onClose={() => setNoteModal(false)} title={t("createFromNote")} wide>
        <div className="space-y-4">
          <p className="text-sm text-stone-500">{t("selectNoteHint")}</p>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-stone-500">{t("patient")}</label>
            <ManagedSelect listKey="patients" value={notePatient} onChange={onNotePatient} className={`${inputCls} mt-1.5`} data-testid="note-patient-select">
              <option value="">{t("selectPatient")}</option>
              {byFirstName(patients).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </ManagedSelect>
          </div>
          {notePatient && (
            <div className="space-y-2 max-h-[45vh] overflow-y-auto custom-scroll" data-testid="billing-notes-list">
              {loadingNotes ? <p className="text-sm text-stone-400 py-6 text-center">…</p>
                : billingNotes.length === 0 ? <p className="text-sm text-stone-400 py-6 text-center">{t("noNotesForPatient")}</p>
                : billingNotes.map((n) => (
                  <button key={n.id} type="button" onClick={() => applyNote(n)} data-testid={`billing-note-${n.id}`}
                    className="w-full text-left p-3 rounded-md border border-border hover:border-moneygreen-500 hover:bg-moneygreen-50 transition-colors duration-200 group">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge tone="green">{t("session")}</Badge>
                        <span className="text-xs font-mono text-stone-500">{fmtDate(n.visit_date || (n.created_at || "").slice(0, 10))}</span>
                        {n.icd10 && <span className="text-xs text-stone-500">· ICD-10 {n.icd10}</span>}
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-moneygreen-600 shrink-0" />
                    </div>
                    {n.reason_for_visit && <p className="text-sm font-semibold text-moneygreen-800 mt-1">{n.reason_for_visit}</p>}
                    {n.preview && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{n.preview}</p>}
                    {n.attending_provider && <p className="text-xs text-stone-400 mt-1">{t("attendingProvider")}: {n.attending_provider}</p>}
                  </button>
                ))}
            </div>
          )}
        </div>
      </Modal>

      <AddIcdCodeModal open={addIcdOpen} onClose={() => setAddIcdOpen(false)}
        onAdded={(c) => { setIcdCatalog((list) => [...list, c]); setInv((s) => ({ ...s, icd10: c.code })); }} />
    </div>
  );
}

const CField = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-stone-500 mb-0.5">{label}</label>
    {children}
  </div>
);

const VRow = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <span className="w-36 shrink-0 text-xs font-semibold text-stone-500">{label}:</span>
    <span className="flex-1 text-moneygreen-800 font-medium">{value || "—"}</span>
  </div>
);
